import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { useAppSelector } from './store';
import AuthWrapper from './features/auth/components/AuthWrapper';
import { ContentUploader } from './features/contentUploader';

// Auth Pages
import GuestWelcome from './pages/GuestWelcome';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GoogleAuthCallback from './pages/auth/GoogleAuthCallback';

// App Pages
import Discover from './pages/Discover';
import ContentDetail from './pages/ContentDetail';
import UserProfile from './pages/UserProfile';
import TakePhoto from './pages/TakePhoto';
import EditProfile from './pages/EditProfile';
import Notifications from './pages/Notifications';
import Followers from './pages/Followers';
import Following from './pages/Following';
import Settings from './pages/Settings';

// Route guard for protected routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Route guard for guest-only routes
const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  if (isAuthenticated) {
    return <Navigate to="/discover" replace />;
  }
  
  return <>{children}</>;
};

// Public routes don't need any guard
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// Create a router layout component that includes AuthWrapper
const Root: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  return (
    <AuthWrapper>
      {/* Only show ContentUploader for authenticated users */}
      {isAuthenticated && <ContentUploader />}
      <div style={{ paddingTop: isAuthenticated ? '0' : '0' }}>
        <Outlet />
      </div>
    </AuthWrapper>
  );
};

// Create router with routes
const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      // Public routes (available to everyone)
      {
        path: '/terms-of-use',
        element: (
          <PublicRoute>
            <TermsOfUse />
          </PublicRoute>
        ),
      },
      {
        path: '/privacy-policy',
        element: (
          <PublicRoute>
            <PrivacyPolicy />
          </PublicRoute>
        ),
      },
      
      // Guest routes (only for non-authenticated users)
      {
        path: '/',
        element: (
          <GuestRoute>
            <GuestWelcome />
          </GuestRoute>
        ),
      },
      {
        path: '/login',
        element: (
          <GuestRoute>
            <Login />
          </GuestRoute>
        ),
      },
      {
        path: '/signup',
        element: (
          <GuestRoute>
            <Signup />
          </GuestRoute>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <GuestRoute>
            <ForgotPassword />
          </GuestRoute>
        ),
      },
      
      // Protected routes (only for authenticated users)
      {
        path: '/discover',
        element: (
          <ProtectedRoute>
            <Discover />
          </ProtectedRoute>
        ),
      },
      {
        path: '/discover/:contentId',
        element: (
          <ProtectedRoute>
            <Discover />
          </ProtectedRoute>
        ),
      },
      {
        path: '/take-photo',
        element: (
          <ProtectedRoute>
            <TakePhoto />
          </ProtectedRoute>
        ),
      },
      {
        path: '/content/:id',
        element: (
          <ProtectedRoute>
            <ContentDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: '/profile/:userId',
        element: (
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit-profile',
        element: (
          <ProtectedRoute>
            <EditProfile />
          </ProtectedRoute>
        ),
      },
      {
        path: '/notifications',
        element: (
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        ),
      },
      {
        path: '/followers/:userId',
        element: (
          <ProtectedRoute>
            <Followers />
          </ProtectedRoute>
        ),
      },
      {
        path: '/following/:userId',
        element: (
          <ProtectedRoute>
            <Following />
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        ),
      },
      
      // Catch-all redirect
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  },
  
  // Auth callback routes (outside of AuthWrapper)
  {
    path: '/sign-in/google/callback',
    element: <GoogleAuthCallback />,
  },
]);

// Router component
export const Router: React.FC = () => {
  return <RouterProvider router={router} />;
}; 